import React, { useState, useEffect, useRef } from "react";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// VoidRequestModal — 300-char reason with live countdown that
// turns red below 50 chars. Used by:
//  • Finance Leader / Team Member submitting a Void Request
//  • Super/Limited Admin rejecting a Void Request (reused as
//    the "Reject reason" modal for symmetry)
// The modal is generic: parent passes `title`, `helpText`,
// `submitLabel`, and `onSubmit(reason)`. It does NOT know about
// giving vs expense — the parent wires that.
// ============================================================

const MAX_LEN = 300;
const RED_BELOW = 50; // Countdown turns red when remaining chars < this

export default function VoidRequestModal({
  open,
  title = "Submit Void Request",
  helpText = "Explain why this record should be voided. This reason will be reviewed by an Admin.",
  submitLabel = "Submit Request",
  danger = false,
  onSubmit,
  onClose,
  testidPrefix = "void-request",
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
      setSubmitting(false);
      // autofocus after paint
      setTimeout(() => textareaRef.current && textareaRef.current.focus(), 40);
    }
  }, [open]);

  if (!open) return null;

  const trimmed = reason.trim();
  const remaining = MAX_LEN - reason.length;
  const countdownRed = remaining < RED_BELOW;
  const canSubmit = trimmed.length > 0 && reason.length <= MAX_LEN && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(trimmed);
    } catch (e) {
      setError(e?.message || "Submit failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
      data-testid={`${testidPrefix}-modal`}
    >
      <div
        className="modal"
        style={{ maxWidth: 520, width: "min(96vw, 520px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <CustomIcon name={danger ? "warning" : "document"} size={18} /> {title}
          </h3>
          <button
            className="modal-close"
            onClick={onClose}
            data-testid={`${testidPrefix}-close`}
          >×</button>
        </div>
        <div className="modal-body" style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: "var(--kc-sub)", marginBottom: 10 }}>
            {helpText}
          </div>
          <label
            className="form-label"
            style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            Reason (required)
          </label>
          <textarea
            ref={textareaRef}
            className="form-input"
            rows={5}
            maxLength={MAX_LEN}
            placeholder="Type a clear, specific reason…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`${testidPrefix}-reason`}
            style={{ width: "100%", resize: "vertical", fontFamily: "'Inter',sans-serif", minHeight: 100 }}
          />
          <div
            style={{
              marginTop: 6,
              display: "flex",
              justifyContent: "flex-end",
              fontSize: 12,
              fontWeight: 600,
              color: countdownRed ? "#f87171" : "var(--kc-sub)",
              transition: "color 0.15s",
            }}
            data-testid={`${testidPrefix}-countdown`}
          >
            {remaining} character{remaining === 1 ? "" : "s"} remaining
          </div>
          {error && (
            <div
              style={{ marginTop: 8, color: "#f87171", fontSize: 12 }}
              data-testid={`${testidPrefix}-error`}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            padding: 14,
            borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)",
            justifyContent: "flex-end",
          }}
        >
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={submitting}
            data-testid={`${testidPrefix}-cancel`}
          >Cancel</button>
          <button
            className="btn"
            onClick={submit}
            disabled={!canSubmit}
            data-testid={`${testidPrefix}-submit`}
            style={{
              background: canSubmit ? (danger ? "#f87171" : "var(--kc-accent)") : "color-mix(in srgb, var(--kc-accent) 30%, transparent)",
              color: "#111",
              fontWeight: 800,
              minHeight: 44,
              padding: "0 22px",
              borderRadius: 10,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {submitting ? "Submitting…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
